import {FC} from "react"
import {Alert, Badge, Card, Carousel, Col, Container, Row} from "react-bootstrap"
import {useTranslation} from "react-i18next"

type GuideItem = {
    id: string
    image: string
}

type GuideCardProps = {
    accepted: boolean
    title: string
    introduction: string
    items: GuideItem[]
}

const GuideCard: FC<GuideCardProps> = ({
    accepted,
    title,
    introduction,
    items,
}) => {
    const {t} = useTranslation("admin")
    const translationPrefix = accepted ? "photoIdGuide.accepted.items" : "photoIdGuide.rejected.items"

    return (
        <Card className={`h-100 border-${accepted ? "success" : "danger"}`}>
            <Card.Body className="d-flex flex-column">
            <div className="d-flex align-items-center gap-2 mb-3">
                <i
                    aria-hidden="true"
                    className={`bi bi-${accepted ? "check-circle-fill text-success" : "x-circle-fill text-danger"} fs-4`}
                />
                <Card.Title className="mb-0">{title}</Card.Title>
                <Badge bg={accepted ? "success" : "danger"} className="ms-auto">
                    {accepted ? "✓" : "×"}
                </Badge>
            </div>
            <Card.Text>{introduction}</Card.Text>

                <Carousel
                    className="carousel-dark mt-auto"
                    interval={6000}
                    pause="hover"
                    touch
                    aria-label={title}
                >
                    {items.map(item => (
                        <Carousel.Item key={item.id}>
                            <div className="text-center px-5">
                                <h3 className="h5 mb-3">{t(`${translationPrefix}.${item.id}.title`)}</h3>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{height: "clamp(18rem, 34vw, 30rem)"}}
                                >
                                    <img
                                        src={item.image}
                                        alt={t(`${translationPrefix}.${item.id}.imageAlt`)}
                                        loading="lazy"
                                        className="mw-100 mh-100"
                                        style={{objectFit: "contain"}}
                                    />
                                </div>
                                <p className="mt-3 mb-4 px-md-4">
                                    {t(`${translationPrefix}.${item.id}.description`)}
                                </p>
                            </div>
                        </Carousel.Item>
                    ))}
                </Carousel>
            </Card.Body>
        </Card>
    )
}

const acceptedItems: GuideItem[] = [
    {id: "personalausweis", image: "/images/photo-id-guide/items/personalausweis.png"},
    {id: "provisionalId", image: "/images/photo-id-guide/items/provisional-id.png"},
    {id: "passport", image: "/images/photo-id-guide/items/passport.png"},
    {id: "drivingLicence", image: "/images/photo-id-guide/items/driving-licence.png"},
    {id: "residencePermit", image: "/images/photo-id-guide/items/residence-permit.png"},
    {id: "cyrillicId", image: "/images/photo-id-guide/items/cyrillic-id.png"},
    {id: "greekId", image: "/images/photo-id-guide/items/greek-id.png"},
]

const rejectedItems: GuideItem[] = [
    {id: "healthInsuranceCard", image: "/images/photo-id-guide/items/health-insurance-card.png"},
    {id: "atheneCard", image: "/images/photo-id-guide/items/athene-card.png"},
    {id: "tudaAppId", image: "/images/photo-id-guide/items/tuda-app-id.png"},
]

const PhotoIdGuideComponent: FC = () => {
    const {t} = useTranslation("admin")

    return (
        <div className="component">
            <Container className="py-4">
                <div className="mb-4">
                    <h1 className="h2">{t("photoIdGuide.title")}</h1>
                    <p className="lead mb-0">{t("photoIdGuide.description")}</p>
                </div>

                <Alert variant="info" className="d-flex gap-3 align-items-start">
                    <i aria-hidden="true" className="bi bi-info-circle-fill fs-4" />
                    <div>
                        <Alert.Heading className="h5">{t("photoIdGuide.requirements.title")}</Alert.Heading>
                        <p className="mb-0">{t("photoIdGuide.requirements.text")}</p>
                    </div>
                </Alert>

                <Row xs={1} xl={2} className="g-4 mt-1">
                    <Col>
                        <GuideCard
                            accepted
                            title={t("photoIdGuide.accepted.title")}
                            introduction={t("photoIdGuide.accepted.introduction")}
                            items={acceptedItems}
                        />
                    </Col>
                    <Col>
                        <GuideCard
                            accepted={false}
                            title={t("photoIdGuide.rejected.title")}
                            introduction={t("photoIdGuide.rejected.introduction")}
                            items={rejectedItems}
                        />
                    </Col>
                </Row>

                <p className="text-body-secondary small mt-3 mb-0">
                    <i aria-hidden="true" className="bi bi-image me-2" />
                    {t("photoIdGuide.imageDisclaimer")}
                </p>
            </Container>
        </div>
    )
}

export default PhotoIdGuideComponent
